import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../../store/appStore';
import { useI18n } from '../../i18n';
import { client } from '../../api/client';
import { useToast } from '../common/Toast';

interface Review {
  id: number;
  rating: number;
  comment: string | null;
  createdAt: string;
  reviewer: {
    id: string;
    name: string;
    type: string;
  };
}

interface ReviewSummary {
  average: number;
  total: number;
  distribution: Record<number, number>;
}

interface ReviewsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';

interface CompanyReviewsProps {
  companyUsername: string;
  companyName: string;
  companyId: string;
}

export function CompanyReviews({ companyUsername, companyName, companyId }: CompanyReviewsProps) {
  const { isAuthenticated, user } = useAppStore();
  const { language } = useI18n();
  const toast = useToast();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [pagination, setPagination] = useState<ReviewsPagination | null>(null);
  const [userReview, setUserReview] = useState<{ id: number; rating: number; comment: string | null; createdAt: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [currentPage, setCurrentPage] = useState(1);

  // Review form state
  const [showForm, setShowForm] = useState(false);
  const [formRating, setFormRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formComment, setFormComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortBy
      });
      const res = await client.api.fetch(`/api/company/${companyUsername}/reviews?${params}`);
      const data = await res.json();

      if (data.success) {
        setReviews(data.reviews || []);
        setSummary(data.summary || null);
        setPagination(data.pagination || null);
        setUserReview(data.userReview || null);

        // Pre-fill form if user already has a review
        if (data.userReview) {
          setFormRating(data.userReview.rating);
          setFormComment(data.userReview.comment || '');
        }
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [companyUsername, currentPage, sortBy]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  const handleSubmitReview = async () => {
    if (formRating < 1 || formRating > 10) {
      toast.error(language === 'sr' ? 'Izaberite ocenu od 1 do 10' : 'Please select a rating from 1 to 10');
      return;
    }

    setSubmitting(true);
    try {
      const res = await client.api.fetch(`/api/company/${companyUsername}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: formRating, comment: formComment.trim() })
      });
      const data = await res.json();

      if (data.success) {
        toast.success(data.message || (language === 'sr' ? 'Ocena je ostavljena' : 'Review submitted'));
        setShowForm(false);
        fetchReviews();
      } else {
        toast.error(data.error || (language === 'sr' ? 'Greška pri slanju ocene' : 'Error submitting review'));
      }
    } catch (err) {
      toast.error(language === 'sr' ? 'Greška pri slanju ocene' : 'Error submitting review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId: number) => {
    if (!confirm(language === 'sr' ? 'Da li ste sigurni da želite da obrišete ocenu?' : 'Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const res = await client.api.fetch(`/api/company/${companyUsername}/reviews/${reviewId}`, {
        method: 'DELETE'
      });
      const data = await res.json();

      if (data.success) {
        toast.success(language === 'sr' ? 'Ocena je obrisana' : 'Review deleted');
        setUserReview(null);
        setFormRating(0);
        setFormComment('');
        fetchReviews();
      } else {
        toast.error(data.error || (language === 'sr' ? 'Greška pri brisanju' : 'Error deleting review'));
      }
    } catch (err) {
      toast.error(language === 'sr' ? 'Greška pri brisanju' : 'Error deleting review');
    }
  };

  const canReview = isAuthenticated && user && user.id !== companyId;

  const sortOptions: { value: SortOption; labelSr: string; labelEn: string }[] = [
    { value: 'newest', labelSr: 'Najnovije', labelEn: 'Newest' },
    { value: 'oldest', labelSr: 'Najstarije', labelEn: 'Oldest' },
    { value: 'highest', labelSr: 'Najviša ocena', labelEn: 'Highest rated' },
    { value: 'lowest', labelSr: 'Najniža ocena', labelEn: 'Lowest rated' },
  ];

  // Star rating display component
  const StarRating = ({ rating, size = 'md', interactive = false, onRate, onHover, onLeave }: {
    rating: number;
    size?: 'sm' | 'md' | 'lg';
    interactive?: boolean;
    onRate?: (r: number) => void;
    onHover?: (r: number) => void;
    onLeave?: () => void;
  }) => {
    const sizeMap = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' };
    const iconSize = sizeMap[size];

    return (
      <div className="flex items-center gap-0.5" onMouseLeave={onLeave}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => onRate?.(star)}
            onMouseEnter={() => onHover?.(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} focus:outline-none`}
          >
            <svg
              className={`${iconSize} ${star <= rating ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600'} transition-colors`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div id="reviews" className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            {language === 'sr' ? 'Ocene i komentari' : 'Reviews & Ratings'}
          </h2>
          {canReview && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              {userReview
                ? (language === 'sr' ? 'Izmeni ocenu' : 'Edit review')
                : (language === 'sr' ? 'Ostavi ocenu' : 'Leave a review')}
            </button>
          )}
        </div>

        {/* Review Form */}
        <AnimatePresence>
          {showForm && canReview && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6 mb-6 border border-slate-200 dark:border-slate-600">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  {userReview
                    ? (language === 'sr' ? 'Izmenite vašu ocenu' : 'Edit your review')
                    : (language === 'sr' ? `Ocenite ${companyName}` : `Rate ${companyName}`)}
                </h3>

                {/* Rating selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Vaša ocena' : 'Your rating'}: {(hoverRating || formRating) > 0 && (
                      <span className="text-amber-500 font-bold">{hoverRating || formRating}/10</span>
                    )}
                  </label>
                  <StarRating
                    rating={hoverRating || formRating}
                    size="lg"
                    interactive
                    onRate={(r) => setFormRating(r)}
                    onHover={(r) => setHoverRating(r)}
                    onLeave={() => setHoverRating(0)}
                  />
                </div>

                {/* Comment */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Komentar (opciono)' : 'Comment (optional)'}
                  </label>
                  <textarea
                    value={formComment}
                    onChange={(e) => setFormComment(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder={language === 'sr' ? 'Podelite vaše iskustvo sa ovom kompanijom...' : 'Share your experience with this company...'}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  />
                  <div className="text-xs text-slate-400 mt-1 text-right">{formComment.length}/2000</div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSubmitReview}
                    disabled={submitting || formRating < 1}
                    className="px-6 py-2.5 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting
                      ? (language === 'sr' ? 'Slanje...' : 'Submitting...')
                      : userReview
                        ? (language === 'sr' ? 'Ažuriraj ocenu' : 'Update review')
                        : (language === 'sr' ? 'Pošalji ocenu' : 'Submit review')}
                  </button>
                  <button
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  >
                    {language === 'sr' ? 'Otkaži' : 'Cancel'}
                  </button>
                  {userReview && (
                    <button
                      onClick={() => handleDeleteReview(userReview.id)}
                      className="ml-auto px-4 py-2 text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      {language === 'sr' ? 'Obriši ocenu' : 'Delete review'}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Summary + Distribution */}
        {summary && summary.total > 0 ? (
          <div className="flex flex-col md:flex-row gap-8 mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
            {/* Average */}
            <div className="flex flex-col items-center justify-center min-w-[140px]">
              <div className="text-5xl font-bold text-amber-500 mb-1">{summary.average}</div>
              <StarRating rating={Math.round(summary.average)} size="sm" />
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {summary.total} {summary.total === 1
                  ? (language === 'sr' ? 'ocena' : 'review')
                  : (language === 'sr' ? 'ocena' : 'reviews')}
              </div>
            </div>

            {/* Distribution bars */}
            <div className="flex-1 space-y-1.5">
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((rating) => {
                const count = summary.distribution[rating] || 0;
                const percentage = summary.total > 0 ? (count / summary.total) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-right text-slate-600 dark:text-slate-400 font-medium">{rating}</span>
                    <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-amber-400 h-full rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-slate-500 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 mb-6 border-b border-slate-200 dark:border-slate-700">
            <svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            <p className="text-slate-500 dark:text-slate-400">
              {language === 'sr' ? 'Još nema ocena za ovu kompaniju.' : 'No reviews yet for this company.'}
            </p>
            {canReview && !showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-3 text-amber-600 hover:text-amber-500 font-medium text-sm"
              >
                {language === 'sr' ? 'Budite prvi koji će ostaviti ocenu!' : 'Be the first to leave a review!'}
              </button>
            )}
          </div>
        )}

        {/* Sort + Reviews list */}
        {summary && summary.total > 0 && (
          <>
            {/* Sort bar */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {language === 'sr' ? `Prikazano ${reviews.length} od ${summary.total}` : `Showing ${reviews.length} of ${summary.total}`}
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 border-0 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {language === 'sr' ? opt.labelSr : opt.labelEn}
                  </option>
                ))}
              </select>
            </div>

            {/* Reviews list */}
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={`p-4 rounded-lg border transition-colors ${
                    user && review.reviewer.id === user.id
                      ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                      : 'border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center text-slate-700 dark:text-slate-300 font-semibold text-sm flex-shrink-0">
                        {review.reviewer.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900 dark:text-white text-sm">
                            {review.reviewer.name}
                          </span>
                          {user && review.reviewer.id === user.id && (
                            <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
                              {language === 'sr' ? 'Vaša ocena' : 'Your review'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StarRating rating={review.rating} size="sm" />
                          <span className="text-xs font-medium text-amber-600">{review.rating}/10</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">
                        {new Date(review.createdAt).toLocaleDateString(language === 'sr' ? 'sr-RS' : 'en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      {user && review.reviewer.id === user.id && (
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title={language === 'sr' ? 'Obriši' : 'Delete'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  {review.comment && (
                    <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed pl-[52px]">
                      {review.comment}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum = i + 1;
                  if (pagination.totalPages > 5) {
                    if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-amber-500 text-slate-900'
                          : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}

        {/* Login prompt for non-authenticated users */}
        {!isAuthenticated && summary && summary.total === 0 && (
          <div className="text-center py-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {language === 'sr'
                ? 'Prijavite se da biste ostavili ocenu.'
                : 'Log in to leave a review.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact star rating display for use in headers/cards
 */
export function CompanyRatingBadge({ average, total, size = 'md', onClick }: {
  average: number;
  total: number;
  size?: 'sm' | 'md';
  onClick?: () => void;
}) {
  const { language } = useI18n();

  if (total === 0) return null;

  const sizeClasses = size === 'sm' ? 'text-xs gap-1' : 'text-sm gap-1.5';
  const starSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center ${sizeClasses} text-slate-600 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <svg className={`${starSize} text-amber-400`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      <span className="font-semibold text-amber-600">{average}</span>
      <span>({total} {total === 1
        ? (language === 'sr' ? 'ocena' : 'review')
        : (language === 'sr' ? 'ocena' : 'reviews')})</span>
    </button>
  );
}
